import { Alert, AlertTitle, Box, Button, Paper, Typography, useTheme } from '@mui/material'
import { Form, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import ShipmentInfo from './Components/ShipmentInfo';
import BookingInfo from './Components/BookingInfo';
import * as Yup from "yup";
import Loader from '../../../components/Loader';
import { useDispatch, useSelector } from 'react-redux';
import { addManageRefreshAddUpdate ,getMangeDataById,updateManageByIdRefresh} from '../../../actions/mangeAction';
import { showSuccessToast } from '../../../slices/toastSlice';
import { resetData } from '../../../slices/manageSlice';
import { getUsersData } from '../../../actions/getUsersDataAction';
import ContainerInfo from './Components/ContainerInfo';
import ExporterInfo from './Components/ExporterInfo';
import ConsigneeInfo from './Components/ConsigneeInfo';

function AddShipment() {

  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [initialValues, setInitialValues] = useState(null);
  const [initialBooking , setInitialBooking] = useState(null);
  const bookingFromState = location?.state?.booking || null;

  const objectById = useSelector((state) => state.manage.objectById);

  const theme = useTheme();
  const { updated, added, hasError, errorMessage, loading } = useSelector((state) => state.manage) || {};


  const dispatch = useDispatch();

  const shipmentId = searchParams.get("id") || null;
  const shipmentMode = searchParams.get("shipmentMode") || null;

  const defaultInitialValues = { 
      entryDate: new Date().toISOString().substr(0, 10), // today's date
      booking: null,
      primaryClient: null,
      clientExporter: null,
      consignee: null,
      consigneeDock: null,
      notifyParty: null,
      primaryBillOfLadingFile: [],
      exporter: null,
      primaryDescription: 'Used Vehicles',
      shipmentMode: shipmentMode,
      shipmentClients: [],
      containers: [{ containerSize: null, containerNumber: null, sealNumber: 0, shipmentItems: [] }]
 }

 const validationSchema = Yup.object({
    entryDate: Yup.date().required('Entry date is required'),
    shipmentMode: Yup.string().oneOf(['FCL','LCL','SPL']).required('Shipment mode is required'),
    primaryDescription: Yup.string().required('Description is required'),
    primaryClient: Yup.object().required('Client is required'),
    clientExporter: Yup.object().required('Client exporter is required'),
    exporter: Yup.object().required('Exporter is required'),
    consignee: Yup.object().when('shipmentMode', {
      is: (mode) => ['FCL', 'LCL'].includes(mode),
      then: (schema) => schema.required('Consignee is required'),
      otherwise: (schema) => schema.nullable(),
    }),
    consigneeDock: Yup.object().when('shipmentMode', {
      is: (mode) => ['FCL', 'LCL'].includes(mode),
      then: (schema) => schema.required('Consignee dock is required'),
      otherwise: (schema) => schema.nullable(),
    }),
    notifyParty: Yup.object().when('shipmentMode', {
      is: (mode) => ['FCL', 'LCL'].includes(mode),
      then: (schema) => schema.required('Notify party is required'),
      otherwise: (schema) => schema.nullable(),
    }),
    shipmentClients: Yup.array()
      .of(Yup.object().nullable())
      .when('shipmentMode', {
        is: 'LCL',
        then: (schema) => schema.min(1, 'At least one secondary client is required for LCL shipments'),
        otherwise: (schema) => schema.nullable(),
      }),
    primaryBillOfLadingFile: Yup.array().nullable(),
    containers: Yup.array()
      .of(Yup.object().shape({
        containerSize: Yup.string().required('Container size is required'),
        //containerNumber: Yup.string().required('Container number is required'),
        //sealNumber: Yup.string().required('Seal number is required'),
        netWeight: Yup.number().min(0, 'Net weight must be positive').required('Net weight is required'),
        shipmentItems: Yup.array()
          .of(Yup.object().shape({
            vehicle: Yup.object().required('Vehicle is required'),
            consignee: Yup.object().test({
              name: 'consignee-required-spl',
              message: 'Consignee is required for SPL shipments',
              test: function(value) {
                const shipmentMode = this.parent?._shipmentMode || this.options.context?.shipmentMode;
                if (shipmentMode === 'SPL') {
                  return value !== null && value !== undefined;
                }
                return true;
              }
            }).nullable(),
            notifyParty: Yup.object().test({
              name: 'notifyParty-required-spl',
              message: 'Notify Party is required for SPL shipments',
              test: function(value) {
                const shipmentMode = this.parent?._shipmentMode || this.options.context?.shipmentMode;
                if (shipmentMode === 'SPL') {
                  return value !== null && value !== undefined;
                }
                return true;
              }
            }).nullable(),
            consigneeDock: Yup.object().test({
              name: 'consigneeDock-required-spl',
              message: 'Consignee Dock is required for SPL shipments',
              test: function(value) {
                const shipmentMode = this.parent?._shipmentMode || this.options.context?.shipmentMode;
                if (shipmentMode === 'SPL') {
                  return value !== null && value !== undefined;
                }
                return true;
              }
            }).nullable(),
            description: Yup.string().nullable(),
            billOfLadingFile: Yup.array().nullable(),
          }).test({
            name: 'shipment-item-spl-validation',
            message: 'Each shipment item must have Consignee, Notify Party, and Consignee Dock for SPL shipments',
            test: function(item) {
              const shipmentMode = this.parent?._shipmentMode;
              if (shipmentMode === 'SPL') {
                if (!item?.consignee || !item?.notifyParty || !item?.consigneeDock) {
                  return false;
                }
              }
              return true;
            }
          }))
          .min(1, 'At least one vehicle is required').required('At least one vehicle is required'),
      }))
      .min(1, 'At least one container is required')
      .required('Containers are required')
      .test({
        name: 'containers-spl-parties-validation',
        message: 'Each shipment item must have Consignee, Notify Party, and Consignee Dock for SPL shipments',
        test: function(containers) {
          if (this.parent?.shipmentMode !== 'SPL') return true;
          return (containers || []).every((container) =>
            (container?.shipmentItems || []).every((item) =>
              item?.consignee && item?.notifyParty && item?.consigneeDock
            )
          );
        },
      }),
    booking: Yup.object().shape({
    
    bookingStatus: Yup.object().shape({
      name: Yup.string(),
    }),
    id: Yup.string().when('bookingStatus.name', {
      is: 'BOOKED',
      then: (schema) => schema.required('Booking ID is required'),
    }),
    status: Yup.string().when('bookingStatus.name', {
      is: 'BOOKED',
      then: (schema) => schema.required('Booking status is required'),
    }),
    cutoffDate: Yup.date().nullable(),
    latestReturnDate: Yup.date().nullable(),
    shippingInstructionsSent: Yup.mixed().nullable(),
    containerGateInDate: Yup.mixed().nullable(),
  }).test({
    name: 'booking-rules',
    skipAbsent: true,
    test(booking, ctx) {
      if (!booking) return true;


      const now = new Date();
      const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());


      if (booking.bookingStatus?.id !== 1 &&  Number(initialBooking?.id) !== Number(booking.id)) {
        // If booking status is not BOOKED, return an error
        return ctx.createError({
          path: `booking`,
          message: 'Booking status must be UNASSIGNED',
        });
      }


      

      // شرط 1: إذا ما في shippingInstructionsSent
      if (
        booking.shippingInstructionsSent === null &&
        booking.cutoffDate &&
        booking.cutoffDate < currentDate
      ) {
        
        return ctx.createError({
          path: `booking`,
          message: 'The booking you selected has passed the last date to send shipping documents to the carrier. Please select a different booking.',
        });
      }

      // شرط 2: إذا ما في containerGateInDate
      if (
        booking.containerGateInDate === null &&
        booking.latestReturnDate &&
        booking.latestReturnDate < currentDate
      ) {
        return ctx.createError({
          path: `booking`,
          message: 'The booking you selected has passed the last loading date. Please select a different booking.',
        });
      }

      return true;
    },
  }),
});






useEffect(() => {


    if (shipmentId) {


      if (objectById.loading === "succeeded") {

        setInitialBooking(objectById.content.booking);

        


        const content = objectById.content || {};
        // Build a fully new object (avoid mutating RTK frozen state)
        // Derive root-level parties from existing shipment or first container (migration support)




        const currentShipmentMode = shipmentMode || content.shipmentMode || content.type || null;
        const primaryClientShipmentItem = (content.containers || [])
          .flatMap(container => container?.shipmentItems || [])
          .find(item => Number(item?.vehicle?.clientId) === Number(content.primaryClientId));
        const firstShipmentItem = content.containers?.[0]?.shipmentItems?.[0] || null;
        const sharedPartySource = primaryClientShipmentItem || firstShipmentItem;
        const usesRootParties = ['FCL', 'LCL'].includes(currentShipmentMode);
        const consignee = usesRootParties ? content.consignee || sharedPartySource?.consignee || null : null;
        const notifyParty = usesRootParties ? content.notifyParty || sharedPartySource?.notifyParty || null : null;
        const consigneeDock = usesRootParties ? content.consigneeDock || sharedPartySource?.consigneeDock || null : null;
        const cloned = {
          ...content,
          id: content.id,
          primaryClient: {id :content.primaryClientId , name : content.primaryClientName,company : content.primaryClientCompany , exporter :  content?.primaryClientExporterId? {id :content.primaryClientExporterId ,name: content.primaryClientExporterName}: null} || null,
          clientExporter: content.exporter || null,
          exporter: content.exporter ,
          consignee,
          notifyParty,
          consigneeDock,
          primaryBillOfLadingFile: content.primaryBillOfLadingFile || [],
          primaryDescription: content.primaryDescription || content.description || 'Used Vehicles',
          shipmentMode: currentShipmentMode,
          shipmentClients: content.shipmentClients || [],
          containers: (content.containers || []).map(container => ({
            id: container.id,
            containerSize: container.containerSize ?? null,
            containerNumber: container.containerNumber ?? null,
            sealNumber: container.sealNumber ?? null,
            netWeight: container.netWeight ?? 0,
            shipmentItems: (container.shipmentItems  || []).map((item) => {

              const vehicle = item?.vehicle ?{id: item.vehicle.id, vin: item.vehicle?.vin, make: {name :item?.vehicle?.make}, model: {name:item?.vehicle?.model}, year: item?.vehicle?.year,fuelType:{name : item?.vehicle?.fuelType} , vehicleType :{name:item?.vehicle?.vehicleType} , weight:item?.vehicle?.weight || 0 , price:item?.vehicle?.price || 0
            
              ,client:{id : item?.vehicle?.clientId, name : item?.vehicle?.clientName, company : item?.vehicle?.clientCompany}
            } : null;
              return {
                ...(item && item.vehicle ? item : {}),
                vehicle,
                consignee: item?.consignee ?? content.consignee ?? null,
                notifyParty: item?.notifyParty ?? content.notifyParty ?? null,
                consigneeDock: item?.consigneeDock ?? content.consigneeDock ?? null,
                description: item?.description ?? (content.primaryDescription || content.description || 'Used Vehicles'),
               billOfLadingFile: item?.billOfLadingFile || [],
              };
            }),
          
          }))
        };

        setInitialValues(cloned);
      }






    } else {
      if (bookingFromState) {
        setInitialBooking(bookingFromState);
        setInitialValues({
          ...defaultInitialValues,
          booking: bookingFromState,
        });
      } else {
        setInitialValues(defaultInitialValues);
      }

    }




  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId, objectById.loading, bookingFromState])



  useEffect(() => {
    if (shipmentId) {

      dispatch(getMangeDataById({ mode: "shipment", id: shipmentId }));
    }
  }, [dispatch, shipmentId]);



useEffect(() => {
    dispatch(getUsersData());
  }, [dispatch]);


const handleKeyDown = (event, submitForm) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitForm();
    }
  };

  
  const hasFormErrors = (formik) => {



    return Object.keys(formik.errors).length > 0;
  };
 const handleBack = () => {
    window.history.back();
  }

  const normalizeShipmentPayload = (values) => {
    const normalizedContainers = (values.containers || []).map((container) => {
      const { vehicles, shipmentItems, ...containerRest } = container;
      const items = shipmentItems || [];

      const normalizedItems = (items || []).map((item) => {
        const vehicle = item?.vehicle || item;
        return {
          ...(item && item.vehicle ? item : {}),
          id: item?.id,
          vehicle,
          consignee:  ['FCL', 'LCL'].includes(values.shipmentMode) ? values.consignee : item?.consignee ?? {},
          notifyParty: ['FCL', 'LCL'].includes(values.shipmentMode) ? values.notifyParty : item?.notifyParty ?? {},
          consigneeDock: ['FCL', 'LCL'].includes(values.shipmentMode) ? values?.consigneeDock : item.consigneeDock ?? {},
          description: item?.description ?? values.primaryDescription ?? 'Used Vehicles',
          billOfLadingFile: item?.billOfLadingFile || [],
        };
      });
      return {
        ...containerRest,
        shipmentItems: normalizedItems,
      };
    });

    const normalizedShipmentClients = (values.shipmentClients || [])
      .map((item) => {
        if (!item) return null;
        if (item.client && item.role) return item;
        const client = item?.client || item;
        if (!client?.id) return null;
        return { client: { id: client.id }, role: 'SECONDARY' };
      })
      .filter(Boolean);

    return {
      ...values,
      containers: normalizedContainers,
      shipmentClients: normalizedShipmentClients,
    };
  };

  const handleSubmit = async (values) => {
    const payload = normalizeShipmentPayload(values);




    if (shipmentId) {
      dispatch(updateManageByIdRefresh({ mode: "shipment", id: shipmentId, data: payload }));
    } else {
      dispatch(addManageRefreshAddUpdate({ mode: "shipment", data: payload }));
    }
  };
  

 useEffect(() => {
    if (added || updated) {


      if (added) {
        if (added) {
          dispatch(showSuccessToast(`The shipment has been saved successfully`));


        }
      }

      if (updated) {
        dispatch(showSuccessToast(`The shipment has been updated successfully`));
      }


      navigate('/shipments');
    }
  }, [added, updated, dispatch, navigate]);


  useEffect(() => {
    return () => {
      dispatch(resetData());
    };
  }, [dispatch]);

  if ((shipmentId && (objectById.loading === "pending" || objectById.loading === "idle" )) || !initialValues ) {
      return <Loader />;
    }

  return (
     <Formik onSubmit={handleSubmit}
              validationSchema={validationSchema}
    
              initialValues={initialValues}>
              {(formik) => (<Form>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, mt: { xs: 2, md: 0 } }}>

              <Box sx={{ position: 'relative', width: 48, height: 48 }}>
                <EditCalendarIcon sx={{ color: theme.palette.primary.main, fontSize: 40, position: 'absolute', left: 0, top: 0, zIndex: 1 }} />
                <AddCircleOutlineIcon sx={{ color: theme.palette.secondary.main, fontSize: 24, position: 'absolute', right: -8, bottom: -8, zIndex: 2, bgcolor: theme.palette.background.paper, borderRadius: '50%' }} />
              </Box>
                
              <Typography variant="h4" sx={{ color: '#0B3C5D', fontWeight: 800, letterSpacing: 1 }}>
                {shipmentId ? t('Edit Shipment') : t('Add New Shipment')}
              </Typography>
            </Box>
            <Box>

            <Paper elevation={3} sx={{
              borderRadius: 4,
              boxShadow: '0 4px 24px 0 rgba(11, 60, 93, 0.10)',
              bgcolor: theme.palette.background.paper,
              p: { xs: 2, md: 4 },
              mt: 2,
              maxWidth: 1200,
              mx: 'auto',
            }}>

              <ShipmentInfo disabled={loading==="pending"} />
              
              <BookingInfo disabled={loading==="pending"} />
              <ExporterInfo disabled={loading==="pending"} />
              {['FCL', 'LCL'].includes(formik.values?.shipmentMode) && <ConsigneeInfo disabled={loading==="pending"} 
              clientVal={formik.values.primaryClient}
              />}
              <ContainerInfo disabled={loading==="pending"} />






              

            </Paper>






            </Box>









            {hasFormErrors(formik) || hasError ? (
              <Box className="errorBox" sx={{ maxWidth: 700, mx: 'auto', mt: 3 }}>
                <Alert severity="error" sx={{ borderRadius: 2, boxShadow: '0 2px 8px 0 rgba(217, 179, 16, 0.10)' }}>
                  <AlertTitle sx={{ color: theme.palette.warning.dark }}>Error</AlertTitle>
                  {Object.entries(formik.errors).flatMap(([fieldName, fieldError]) => {
                    // Special handling for containers array to show per-field messages

                    if (fieldName === 'containers' && Array.isArray(fieldError)) {
                      return fieldError.map((containerErr, idx) => {
                        if (!containerErr) return null;
                        const fieldLabels = {
                          containerSize: t('Container Size'),
                          containerNumber: t('Container Number'),
                          sealNumber: t('Seal Number'),
                          netWeight: t('Net Weight'),
                          shipmentItems: t('Shipment Items'),
                        };
                        const messages = Object.entries(fieldLabels)
                          .map(([k, label]) => {
                            const val = containerErr[k];
                            if (!val) return null;
                            if (Array.isArray(val)) {
                              // shipmentItems errors might be array; flatten strings
                              const innerStr = val.filter(v => typeof v === 'string');
                              return innerStr.map((v,i) => `${label}: ${v}`);
                            }
                            if (typeof val === 'string') return `${label}: ${val}`;
                            return null;
                          })
                          .flat()
                          .filter(Boolean);
                        if (messages.length === 0) return null;
                        return (
                          <div key={`container-${idx}`} style={{ marginBottom: 8 }}>
                            <strong>{t('Container')} #{idx + 1}:</strong>
                            <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                              {messages.map((m, i) => (
                                <li key={i}>{m}</li>
                              ))}
                            </ul>
                          </div>
                        );
                      });
                    }
                    // Generic array (fallback)
                    if (Array.isArray(fieldError)) {
                      return fieldError.map((_, i) => (
                        <div key={`${fieldName}-${i}`}>
                          {t('Please check the details of the')} {t(fieldName)} # {i + 1} {t('section')}
                        </div>
                      ));
                    }
                    if (typeof fieldError === 'string') {
                      return <div key={fieldName}>{fieldError}</div>;
                    }
                    if (fieldError && typeof fieldError === 'object') {
                      const firstMsg = Object.values(fieldError).find(v => typeof v === 'string');
                      if (firstMsg) return <div key={fieldName}>{firstMsg}</div>;
                    }
                    return null;
                  })}
                  {['primaryClient','clientExporter','exporter','consignee'].map(party => (
                    typeof formik.errors[party] === 'string' ? <div key={party}>{formik.errors[party]}</div> : null
                  ))}
                  {hasError ? <div>{errorMessage}</div> : ""}
                </Alert>
              </Box>
            ) : (
              ""
            )}


            <Box display="flex" flexDirection={{ xs: 'column', md: 'row-reverse' }} alignItems="center" gap={2} margin={{ xs: '32px 0', md: '64px 49px' }}>
              {loading !== "pending" ? (
                <div onKeyDown={(e) => handleKeyDown(e, formik.submitForm)}>
                  <Button
                    variant="contained"
                    type="submit"
                    sx={{
                      bgcolor: theme.palette.secondary.main,
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                      borderRadius: '10px',
                      width: { xs: '100%', md: '231px' },
                      height: '55px',
                      boxShadow: '0 2px 8px 0 rgba(217, 179, 16, 0.10)',
                      fontSize: 18,
                      mb: { xs: 1, md: 0 },
                      '&:hover': { bgcolor: theme.palette.secondary.dark, color: '#fff' },
                    }}
                  >
                    {t("save")}
                  </Button>
                </div>
              ) : (
                <Loader />
              )}
              <Button
                onClick={() => handleBack()}
                variant="outlined"
                sx={{
                  border: `2px solid ${theme.palette.primary.main}`,
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  borderRadius: '10px',
                  width: { xs: '100%', md: '231px' },
                  height: '55px',
                  fontSize: 18,
                  bgcolor: theme.palette.background.paper,
                  '&:hover': { bgcolor: theme.palette.primary.main, color: '#fff', borderColor: theme.palette.secondary.main },
                }}
              >
                {t("cancel")}
              </Button>
            </Box>







              </Form>)}

              </Formik>




  )
}

export default AddShipment
